import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AIAdvisoryBadge } from "@/components/owner/AIAdvisoryBadge";
import { Loader2, Sparkles, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DESTINATION_LABELS,
  scanListingPhotos,
  summarizeListingScanFailures,
  type ListingItemDestination,
  type ListingRoomScanFailure,
  type ListingScanDetectedItem,
  type ListingScanPhoto,
} from "@/lib/listingPhotoScan";
import { AI_SCAN_EMPTY, sanitizeAiUserMessage } from "@/lib/aiUserFacingErrors";
import {
  type GenerateChecklistItem,
  cleaningItemsFromScan,
  unitCheckItemsFromScan,
} from "@/lib/applyListingScanToChecklist";
import { normalizeRoomName } from "@/lib/roomUtils";

type Phase = "scanning" | "review" | "error";

const CHECKLIST_DESTINATIONS: ListingItemDestination[] = [
  "cleaning_task",
  "unit_check",
  "maintenance",
  "skip",
];

const DESTINATION_LABELS_CHECKLIST: Partial<Record<ListingItemDestination, string>> = {
  cleaning_task: DESTINATION_LABELS.cleaning_task,
  unit_check: DESTINATION_LABELS.unit_check,
  maintenance: "Unit Check — appliance / fixture",
  skip: DESTINATION_LABELS.skip,
};

function toGenerateItem(item: ListingScanDetectedItem): GenerateChecklistItem {
  const isCleaning = item.destination === "cleaning_task";
  return {
    ...item,
    useSourceAsReferencePhoto: isCleaning && !!item.sourcePhotoUrl,
    requireAfterPhoto: false,
  };
}

interface GenerateChecklistFromPhotosModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  photos: ListingScanPhoto[];
  onApply: (items: GenerateChecklistItem[]) => Promise<void>;
  isApplying?: boolean;
}

export function GenerateChecklistFromPhotosModal({
  open,
  onOpenChange,
  propertyId,
  photos,
  onApply,
  isApplying = false,
}: GenerateChecklistFromPhotosModalProps) {
  const [phase, setPhase] = useState<Phase>("scanning");
  const [items, setItems] = useState<GenerateChecklistItem[]>([]);
  const [progress, setProgress] = useState({ done: 0, total: 0, room: "" });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [scanFailures, setScanFailures] = useState<{ roomName: string; message: string }[]>([]);
  const [showLowConfidence, setShowLowConfidence] = useState(false);
  const scanGeneration = useRef(0);

  const runScan = useCallback(async () => {
    const gen = ++scanGeneration.current;
    if (photos.length === 0) {
      setItems([]);
      setScanFailures([]);
      setPhase("review");
      return;
    }
    setPhase("scanning");
    setErrorMessage(null);
    setScanFailures([]);
    try {
      const { items: detected, failures } = await scanListingPhotos(propertyId, photos, (done, total, roomName) => {
        if (gen !== scanGeneration.current) return;
        setProgress({ done, total, room: roomName });
      });
      if (gen !== scanGeneration.current) return;
      setScanFailures(failures);
      const checklistOnly = detected
        .filter(
          (i) =>
            i.destination === "cleaning_task" ||
            i.destination === "unit_check" ||
            i.destination === "maintenance"
        )
        .map(toGenerateItem);
      setItems(checklistOnly);
      setPhase("review");
    } catch (e) {
      if (gen !== scanGeneration.current) return;
      const failures =
        e && typeof e === "object" && "failures" in e
          ? (e as { failures: ListingRoomScanFailure[] }).failures
          : [];
      setScanFailures(failures);
      setErrorMessage(
        e instanceof Error ? sanitizeAiUserMessage(e.message) : sanitizeAiUserMessage("Scan failed")
      );
      setPhase("error");
    }
  }, [propertyId, photos]);

  useEffect(() => {
    if (!open) return;
    setShowLowConfidence(false);
    void runScan();
    return () => {
      scanGeneration.current += 1;
    };
  }, [open, runScan]);

  const LOW_CONF = 0.75;
  const displayed = showLowConfidence
    ? items
    : items.filter((i) => i.confidence >= LOW_CONF);

  const updateItem = (id: string, patch: Partial<GenerateChecklistItem>) => {
    setItems((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i;
        const next = { ...i, ...patch };
        if (patch.destination != null && patch.destination !== "cleaning_task") {
          next.useSourceAsReferencePhoto = false;
          next.requireAfterPhoto = false;
        }
        return next;
      })
    );
  };

  const included = items.filter((i) => i.included && i.name.trim() && i.destination !== "skip");
  const cleaningCount = cleaningItemsFromScan(included).length;
  const unitCount = unitCheckItemsFromScan(included).length;

  const groupedByRoom = useMemo(() => {
    const map = new Map<string, GenerateChecklistItem[]>();
    for (const item of displayed) {
      const key = normalizeRoomName(item.roomName);
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    }
    const roomOrder = [...map.keys()].sort((a, b) => {
      const nameA = map.get(a)?.[0]?.roomName ?? a;
      const nameB = map.get(b)?.[0]?.roomName ?? b;
      return nameA.localeCompare(nameB);
    });
    return roomOrder.map((key) => ({
      key,
      roomLabel: map.get(key)?.[0]?.roomName ?? key,
      items: map.get(key) ?? [],
    }));
  }, [displayed]);

  const handleApply = async () => {
    await onApply(included);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-primary shrink-0" />
            Generate checklist from photos
          </DialogTitle>
          <DialogDescription className="text-left">
            AI scans one photo per room and suggests Unit Check items and cleaning tasks. Review by room,
            then add to this checklist. Nothing is saved until you tap Add to checklist.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-2">
          {phase === "scanning" && (
            <div className="space-y-6 py-10 text-center">
              <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
              <p className="text-sm text-muted-foreground">
                Scanning {photos.length} room{photos.length !== 1 ? "s" : ""}…
                {progress.total > 0 ? (
                  <>
                    {" "}
                    ({progress.done}/{progress.total}
                    {progress.room ? ` — ${progress.room}` : ""})
                  </>
                ) : null}
              </p>
              <AIAdvisoryBadge className="mx-auto" />
            </div>
          )}

          {phase === "error" && (
            <div className="space-y-4 py-4">
              <div className="flex gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
                <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
                <p className="text-sm">{errorMessage}</p>
              </div>
              <Button type="button" variant="outline" onClick={() => void runScan()}>
                Try again
              </Button>
            </div>
          )}

          {phase === "review" && (
            <div className="space-y-4 py-2">
              <AIAdvisoryBadge />

              {scanFailures.length > 0 && items.length > 0 ? (
                <p className="text-sm text-amber-800 dark:text-amber-200 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                  {summarizeListingScanFailures(scanFailures, photos.length)}
                </p>
              ) : null}

              {photos.length === 0 ? (
                <p className="text-sm text-muted-foreground rounded-lg border p-4 bg-muted/30">
                  No room photos found. Upload photos on the Photos tab and assign each to a room, then try
                  again.
                </p>
              ) : items.length === 0 ? (
                <p className="text-sm text-muted-foreground rounded-lg border p-4 bg-muted/30">
                  {scanFailures.length > 0
                    ? summarizeListingScanFailures(scanFailures, photos.length)
                    : AI_SCAN_EMPTY}
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span className="text-muted-foreground">
                      <span className="font-medium text-foreground">{included.length}</span> selected
                      {cleaningCount > 0 || unitCount > 0 ? (
                        <span className="text-xs">
                          {" "}
                          ({unitCount} unit check · {cleaningCount} cleaning)
                        </span>
                      ) : null}
                    </span>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="ai-gen-low-conf"
                        checked={showLowConfidence}
                        onCheckedChange={(c) => setShowLowConfidence(!!c)}
                      />
                      <Label htmlFor="ai-gen-low-conf" className="text-xs font-normal cursor-pointer">
                        Show low-confidence
                      </Label>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {groupedByRoom.map(({ key, roomLabel, items: roomItems }) => (
                      <div key={key} className="space-y-2">
                        <h3 className="text-sm font-semibold sticky top-0 bg-background/95 py-1 z-10">
                          {roomLabel}
                          <span className="text-muted-foreground font-normal ml-2">
                            ({roomItems.filter((i) => i.included).length} selected)
                          </span>
                        </h3>
                        <div className="space-y-2">
                          {roomItems.map((item) => (
                            <div
                              key={item.id}
                              className={cn(
                                "rounded-lg border p-3 space-y-2",
                                !item.included && "opacity-60 bg-muted/40"
                              )}
                            >
                              <div className="flex items-start gap-2">
                                <Checkbox
                                  checked={item.included}
                                  onCheckedChange={(c) => updateItem(item.id, { included: !!c })}
                                  className="mt-1"
                                />
                                <div className="flex-1 min-w-0 space-y-2">
                                  <div className="flex flex-wrap gap-2 items-center">
                                    <Input
                                      className="h-8 text-sm flex-1 min-w-[120px]"
                                      value={item.name}
                                      onChange={(e) => updateItem(item.id, { name: e.target.value })}
                                    />
                                    <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                                      {Math.round(item.confidence * 100)}%
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <div className="space-y-1">
                                      <Label className="text-[10px] text-muted-foreground">Save as</Label>
                                      <Select
                                        value={
                                          item.destination === "maintenance"
                                            ? "maintenance"
                                            : item.destination
                                        }
                                        onValueChange={(v) =>
                                          updateItem(item.id, {
                                            destination: v as ListingItemDestination,
                                          })
                                        }
                                      >
                                        <SelectTrigger className="h-8 text-xs">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {CHECKLIST_DESTINATIONS.map((d) => (
                                            <SelectItem key={d} value={d} className="text-xs">
                                              {DESTINATION_LABELS_CHECKLIST[d] ?? d}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    {item.destination === "cleaning_task" ? (
                                      <div className="space-y-1">
                                        <Label className="text-[10px] text-muted-foreground">Task text</Label>
                                        <Input
                                          className="h-8 text-xs"
                                          value={item.suggestedTask ?? ""}
                                          placeholder={`Clean ${item.name.toLowerCase()}`}
                                          onChange={(e) =>
                                            updateItem(item.id, { suggestedTask: e.target.value })
                                          }
                                        />
                                      </div>
                                    ) : null}
                                  </div>
                                  {item.destination === "cleaning_task" && (
                                    <div className="flex flex-wrap gap-4 pt-1">
                                      <div className="flex items-center gap-2">
                                        <Switch
                                          id={`ref-${item.id}`}
                                          checked={item.useSourceAsReferencePhoto}
                                          disabled={!item.sourcePhotoUrl}
                                          onCheckedChange={(c) =>
                                            updateItem(item.id, { useSourceAsReferencePhoto: c })
                                          }
                                        />
                                        <Label
                                          htmlFor={`ref-${item.id}`}
                                          className="text-[11px] font-normal cursor-pointer"
                                        >
                                          Use listing photo as reference
                                        </Label>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Switch
                                          id={`after-${item.id}`}
                                          checked={item.requireAfterPhoto}
                                          onCheckedChange={(c) =>
                                            updateItem(item.id, { requireAfterPhoto: c })
                                          }
                                        />
                                        <Label
                                          htmlFor={`after-${item.id}`}
                                          className="text-[11px] font-normal cursor-pointer"
                                        >
                                          Require after photo
                                        </Label>
                                      </div>
                                    </div>
                                  )}
                                  {item.destination === "cleaning_task" && item.suggestedTask ? (
                                    <p className="text-[10px] text-muted-foreground leading-snug">
                                      {item.evidence}
                                    </p>
                                  ) : item.evidence ? (
                                    <p className="text-[10px] text-muted-foreground leading-snug">
                                      {item.evidence}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0 gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isApplying}>
            Cancel
          </Button>
          {phase === "review" && (
            <Button
              type="button"
              onClick={() => void handleApply()}
              disabled={isApplying || included.length === 0 || photos.length === 0}
            >
              {isApplying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Add {included.length} to checklist
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
